import React, { useEffect, useState } from "react";
import "./Tools.css";
import { PricingCard, TooltipDomAuth } from "../../common";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { StorageServices, ToolServices } from "../../../services";
import { Dropdown } from "primereact/dropdown";
import TooltipLang from "../../common/components/TooltipLang";
import { Spinner } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";

interface CompanyDomains {
  CompanyId?: number;
  Domains?: any[];
}

const plans = [
  {
    planId: 1,
    titleId: "card1.title",
    subtitleId: "card1.subtitle",
    definitionId: "card1.definition",
    items: [
      "card1.item1",
      "card1.item2",
      "card1.item3",
      "card1.item4",
      "card1.item5",
    ],
  },
  {
    planId: 2,
    titleId: "card2.title",
    subtitleId: "card2.subtitle",
    definitionId: "card2.definition",
    items: [
      "card2.item1",
      "card2.item2",
      "card2.item3",
      "card2.item4",
      "card2.item5",
    ],
  },
  {
    planId: 3,
    titleId: "card3.title",
    subtitleId: "card3.subtitle",
    definitionId: "card3.definition",
    items: [
      "card3.item1",
      "card3.item2",
      "card3.item3",
      "card3.item4",
      "card3.item5",
    ],
  },
];

const Tools: React.FC = () => {
  const companyPlanId = StorageServices.GetCookieUser().Contract.Id || 3;
  const platformId = StorageServices.GetCookieUser().PlatformId;

  const [inputValue, setInputValue] = useState<string>("");
  const [tags, setTags] = useState<string[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [selectedTagIndex, setSelectedTagIndex] = useState<number | null>(null);
  const [newTagIndex, setNewTagIndex] = useState<number | null>(null);
  const [companyDomains, setCompanyDomains] = useState<CompanyDomains>();
  const [selectedLanguage, setSelectedLanguage] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false); // Add this state for the spinner
  const languages = [
    { name: "Español", code: "es" },
    { name: "Portugues", code: "pt" },
    { name: "English", code: "en" },
  ];
  const intl = useIntl();

  const GetDomains = async () => {
    try {
      const response = await ToolServices.GetCompanyDomains();

      if (response && response.Status === 200) {
        return response.Content || {};
      } else {
        return {};
      }
    } catch (error) {
      console.log("ERROR GetDomains", error);
    }
  };

  useEffect(() => {
    GetDomains().then((data) => {
      setCompanyDomains(data);

      if (data && data.Domains) {
        setTags(
          data.Domains.map((element: any, index: number) => {
            return element.Name;
          })
        );
      }

      setNewTagIndex(tags.length);
    });
  }, []);

  const addTag = () => {
    try {
      if (inputValue.trim() !== "") {
        const newTags = [...tags];
        const newCompany = { ...companyDomains };

        if (!newCompany.Domains) newCompany.Domains = new Array<any>();

        const baseItem = {
          Id: 0,
          Name: inputValue
            .trim()
            .replace("http://", "")
            .replace("https://", ""),
        };

        newCompany.Domains.push(baseItem);

        newTags.push(
          inputValue.trim().replace("http://", "").replace("https://", "")
        );

        setCompanyDomains(newCompany);
        setTags(newTags);
        setNewTagIndex(tags.length);
        setInputValue("");
      }
    } catch (error) {
      console.log("ERROR addTag", error);
    }
  };

  const removeTag = (index: number) => {
    try {
      const newTags = tags.filter((_, i) => i !== index);
      const newCompany = { ...companyDomains };
      let newDomains: any[] =
        companyDomains && companyDomains.Domains
          ? companyDomains.Domains.filter((_, i) => i !== index)
          : new Array<any>();

      if (!newDomains) newDomains = new Array<any>();

      newCompany.Domains = newDomains;

      setCompanyDomains(newCompany);
      setTags(newTags);
    } catch (error) {
      console.log("ERROR removeTag", error);
    }
  };

  const handleRemoveClick = (index: number) => {
    setSelectedTagIndex(index);
    setVisible(true);
  };

  const confirmRemoveTag = () => {
    if (selectedTagIndex !== null) {
      removeTag(selectedTagIndex);
      setSelectedTagIndex(null);
    }
    setVisible(false);
  };

  const footerContent = (
    <div>
      <Button
        icon="pi pi-times"
        onClick={() => setVisible(false)}
        className="tools-add-button-modal-white"
      >
        <FormattedMessage id="ready-size-no" defaultMessage="No" />
      </Button>
      <Button
        icon="pi pi-check"
        onClick={confirmRemoveTag}
        autoFocus
        className="tools-add-button-modal"
      >
        <FormattedMessage id="ready-size-yes" defaultMessage="Si" />
      </Button>
    </div>
  );

  const onClickSaveDomains = async () => {
    setIsSaving(true); // Set isSaving to true when saving starts
    try {
      const saveDomains =
        companyDomains && companyDomains.Domains
          ? companyDomains.Domains
          : new Array<any>();

      if (saveDomains && saveDomains.length > 0) {
        const response = await ToolServices.UpdateCompanyDomains(saveDomains);

        if (response && response.Status === 200) {
          GetDomains().then((data) => {
            setCompanyDomains(data);
            if (data && data.Domains) {
              setTags(
                data.Domains.map((element: any) => {
                  return element.Name;
                })
              );
            }
            setNewTagIndex(tags.length);
          });
        } else {
          console.log("ERROR onClickSaveDomains Response", response);
        }
      }

      // Remove the timeout, spinner will stop once the domains are saved
      setIsSaving(false); // Stop the spinner when saving is complete
    } catch (error) {
      console.log("ERROR onClickSaveDomains", error);
      setIsSaving(false); // Set isSaving to false if there's an error
    }
  };

  return (
    <div className="tools-main-container">
      {(platformId === 200 || platformId === 2) && (
        <div className="tools-cards-container">
          <p className="tools-title">
            <FormattedMessage id="ourPlans" />
          </p>
          <div className="cards-main-container">
            {plans.map((plan) => (
              <PricingCard
                key={plan.planId}
                icon={companyPlanId === plan.planId ? faCheckCircle : null}
                titleId={plan.titleId}
                subtitleId={plan.subtitleId}
                definitionId={plan.definitionId}
                items={plan.items}
                isActive={companyPlanId === plan.planId}
              />
            ))}
          </div>
        </div>
      )}
      <div className="tools-authdom-container">
        <p className="tools-auth-domains">
          <FormattedMessage id="authDomains" />
          {/* Dominios Autorizados */}
        </p>
        <div className="tooltip-subtitle-tooltip-container">
          <p className="tools-auth-domains-subtitle">
            <FormattedMessage id="addAuthDomains" />

            {/* Puedes agregar otros sitios autorizados a tu cuenta. */}
          </p>
          <TooltipDomAuth />
        </div>
        <div className="domains-main-container">
          <div className="input-tag-container">
            <InputText
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={intl.formatMessage({
                id: "addDomainPlaceholder",
                defaultMessage: "Agregar Dominio",
              })}
              className="tools-domain-input"
            />
            <Button onClick={addTag} className="tools-add-button">
              <FormattedMessage id="addButtonLabel" defaultMessage="Agregar" />
            </Button>
            <Button
              onClick={onClickSaveDomains}
              className="tools-add-button"
              disabled={isSaving}
            >
              <FormattedMessage
                id="ready-size-Guardar"
                defaultMessage="Guardar"
              />
            </Button>
            {isSaving && (
              <div className="ms-2 tools-spinner-container">
                <Spinner animation="border" role="status" size="sm">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}
          </div>
          <div className="tags-container">
            {tags.map((tag, index) => (
              <Tag
                key={index}
                className={`tag-item-tools p-tag-tools ${
                  index === newTagIndex ? "new" : ""
                }`}
              >
                <div className="flex align-items-center gap-2 ">
                  <span className="text-base ">{tag}</span>
                  <i
                    className="pi pi-times text-xs"
                    onClick={() => handleRemoveClick(index)}
                    style={{ cursor: "pointer" }}
                  ></i>
                </div>
              </Tag>
            ))}
          </div>
        </div>
      </div>
      <Dialog
        header={<FormattedMessage id="alertHeader" defaultMessage="Alerta" />}
        visible={visible}
        style={{ width: "25vw" }}
        onHide={() => setVisible(false)}
        footer={footerContent}
      >
        <p className="m-0">
          <FormattedMessage
            id="confirmDeleteMessage"
            defaultMessage="Estas seguro que queres borrar este dominio?"
          />
        </p>
      </Dialog>

      <div className="tools-authdom-container">
        <p className="tools-auth-domains">
          <FormattedMessage
            id="appLanguage"
            defaultMessage="Idioma Aplicación"
          />
        </p>
        <div className="tooltip-subtitle-tooltip-container">
          <p>
            <FormattedMessage
              id="appLanguage"
              defaultMessage="Que idioma queres que use tu aplicación en el sitio?"
            />
          </p>
          <TooltipLang />
        </div>
        <div className="domains-main-container">
          <Dropdown
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.value)}
            options={languages}
            optionLabel="name"
            placeholder={intl.formatMessage({
              id: "selectLanguagePlaceholder",
              defaultMessage: "Selecciona un idioma",
            })}
            className="w-full md:w-14rem"
          />
          <p className="current-lang">
            <FormattedMessage
              id="toolsCurrentLanguage"
              defaultMessage="Tu idioma actual es:"
            />{" "}
            {selectedLanguage ? selectedLanguage.name : ""}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Tools;
